import { userFacingError } from "../userFacingError";
import { connectorsByIdentifier } from "./index";
import {
  isTriviallyMatchingPattern,
  urlPatternError,
} from "./urlPatternConversion";

export const MAX_URL_PATTERN_LENGTH = 2048;

export function assertValidConnectorUrlPattern(urlPattern: string): void {
  const trimmed = urlPattern.trim();
  if (trimmed.length > MAX_URL_PATTERN_LENGTH) {
    throw userFacingError("URL pattern is too long.");
  }
  const error = urlPatternError(trimmed);
  if (error) throw userFacingError(error);
}

export function normalizeConnectorUrlPattern(
  urlPattern: string,
  identifier: string,
  configuration: Record<string, string>,
  previousConfiguration?: Record<string, string>
): string {
  const definition = connectorsByIdentifier[identifier];
  const derive = (config: Record<string, string>) => {
    const derived = definition?.defaultUrlPattern(config).trim() ?? "";
    return isTriviallyMatchingPattern(derived) ? "" : derived;
  };

  // A pattern still equal to the default for the previous configuration was
  // never customized, so it keeps tracking the default as the configuration
  // (e.g. the domain) changes.
  const trimmed = urlPattern.trim();
  const tracksDefault =
    !trimmed ||
    isTriviallyMatchingPattern(trimmed) ||
    (previousConfiguration !== undefined &&
      trimmed === derive(previousConfiguration));
  return tracksDefault ? derive(configuration) : trimmed;
}

// The validated, trimmed, normalized identity fields every connector write
// shares — one policy for create, update, and variant switch.
export function normalizedConnectorIdentity(fields: {
  name: string;
  identifier: string;
  urlPattern: string;
  configuration: Record<string, string>;
  previousConfiguration?: Record<string, string>;
}) {
  assertValidConnectorUrlPattern(fields.urlPattern);
  const identifier = fields.identifier.trim();
  return {
    name: fields.name.trim(),
    identifier,
    urlPattern: normalizeConnectorUrlPattern(
      fields.urlPattern,
      identifier,
      fields.configuration,
      fields.previousConfiguration
    ),
    configuration: fields.configuration,
  };
}
